import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

/**
 * The app always presents dates in the German format DD.MM.YYYY (with time as
 * HH:mm:ss where needed), whatever the UI language. dayjs format tokens are
 * locale-independent, so these render German-style everywhere.
 */

/**
 * Spelled out rather than taken from a dayjs locale, so the weekday stays
 * German whatever locale is active — like every other date we print.
 */
const GERMAN_WEEKDAYS = [
    'Sonntag',
    'Montag',
    'Dienstag',
    'Mittwoch',
    'Donnerstag',
    'Freitag',
    'Samstag'
] as const;

/**
 * A stored date ('YYYY-MM-DD' or ISO) as 'DD.MM.YYYY'; empty/invalid → ''.
 * Also accepts a `Date` — grid columns typed as `date` hand the formatter the
 * parsed value rather than the stored string (see `dateColumn`).
 */
export function formatDate(
    value: string | number | Date | null | undefined
): string {
    if (!value) {
        return '';
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD.MM.YYYY') : '';
}

/**
 * A day with everything needed to place it in the field season:
 * 'Montag, 12.12.2024 - KW 24'. The week is the ISO one the Zeiterfassung counts
 * in ([[zeiterfassung-timeentry-plan]]). Empty/invalid → ''.
 */
export function formatWeekdayDate(
    value: string | number | Date | null | undefined
): string {
    if (!value) {
        return '';
    }
    const parsed = dayjs(value);
    if (!parsed.isValid()) {
        return '';
    }
    const weekday = GERMAN_WEEKDAYS[parsed.day()];
    return `${weekday}, ${parsed.format('DD.MM.YYYY')} - KW ${parsed.isoWeek()}`;
}

/**
 * A date value back in the STORED 'YYYY-MM-DD' form; empty/invalid → ''.
 *
 * Grid cells typed as `date` (see `dateColumn`) hand their edit value around as
 * a `Date` on entering edit mode but as a 'YYYY-MM-DD' string once the picker
 * has written one. Validators run their input through here so they see the same
 * thing either way.
 */
export function toDayString(value: unknown): string {
    if (!value) {
        return '';
    }
    const parsed = dayjs(value as string | number | Date);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
}

/**
 * A stored ISO datetime as 'DD.MM.YYYY HH:mm:ss'; empty/invalid → ''. Accepts
 * the strings/dates the history timestamps use.
 */
export function formatDateTime(
    value: string | number | Date | null | undefined
): string {
    if (!value) {
        return '';
    }
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm:ss') : '';
}
